<template>
  <div class="auth-page">
    <div class="auth-form-wrapper">
      <c-logo :height="40" class="auth-logo" info />
      <v-divider class="d-none d-md-block" />
      <div class="form-wrapper login-form">
        <v-form ref="form" class="form pass-form" @submit.prevent="handleLogin()">
          <div>
            <span class="size-title">{{ $t('constants.login') }}</span>
            <div class="size-subtitle mt-2">{{ $t('constants.loginIntoAccount') }}</div>

            <c-text-field
              v-model.trim="username"
              :rules="[$rules.required, $rules.whiteSpaces]"
              :label="$t('forms.username') | upperFirst"
              name="username"
              data-cy="username"
              outlined />

            <c-text-field
              v-model.trim="password"
              :rules="[$rules.required, $rules.password, $rules.whiteSpaces]"
              :type="showPass ? 'text' : 'password'"
              :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
              :label="$t('forms.password') | upperFirst"
              name="password"
              data-cy="password"
              outlined
              @click:append="showPass = !showPass" />

            <div class="row">
              <div class="col-12 mt-5 mb-6">
                <v-btn
                  x-large
                  :loading="loginLoading"
                  depressed
                  color="primary"
                  width="100%"
                  data-cy="submit"
                  type="submit">
                  {{ $t('buttons.login') | upperFirst }}
                </v-btn>
              </div>
            </div>

            <div :class="hideEligibility ? 'd-flex justify-center': 'd-flex justify-space-between'">
              <router-button
                v-if="!hideEligibility"
                class="size-medium"
                :text="$t('buttons.checkEligibility') | upperCase"
                to="/check-eligibility"></router-button>

              <router-button
                class="text-right size-medium"
                :text="$t('buttons.forgotPassword') | upperCase"
                to="/forgotpassword"></router-button>
            </div>

            <v-divider class="my-8" />

            <div class="d-flex justify-center mt-5">
              <span class="text size-medium mr-1">
                {{ $t('modals.dontHavePatientAccount') | upperFirst }}
              </span>

              <router-button
                :text="$t('buttons.signUpHere') | upperFirst"
                filter="capitalize"
                class="ml-1 size-medium text-right"
                :external-link="!!(whiteLabel && whiteLabel.signupLink)"
                :to="signUpLink" />
            </div>
          </div>
        </v-form>

        <div class="static-links">
          <StaticLinks />
        </div>
      </div>
    </div>

    <div class="auth-background d-none d-md-flex"></div>
  </div>
</template>

<script>
// TODO why do we validate form when using authorization token
import CLogo from '@/components/ui/CLogo';
import { mapState } from 'vuex';

import RouterButton from '@/views/Login/components/RouterButton';
import StaticLinks from '@/views/Login/components/StaticLinks';
import { login } from '@/common/preload/auth';

// Hide eligibility button for specific white label
const HIDE_ELIGIBILITY_FOR_WL = 'seacret.careclix.com';

export default {
  name: 'LoginPage',
  components: {
    RouterButton,
    StaticLinks,
    CLogo,
  },
  data: () => ({
    username: '',
    password: '',
    loginLoading: false,
    showPass: false,
    savedPrefieldToken: true,
  }),
  computed: {
    ...mapState('loggedAccount', ['account']),
    ...mapState('common', ['whiteLabel']),

    signUpLink() {
      return this.whiteLabel && this.whiteLabel.signupLink ? this.whiteLabel.signupLink : '/register';
    },
    hideEligibility() {
      return this.whiteLabel && this.whiteLabel.domain === HIDE_ELIGIBILITY_FOR_WL;
    },
  },
  mounted() {
    if (this.checkAutoLoginToken()) {
      this.handleLogin({ token: this.$route.query.token });
    } else {
      const prefieldToken = this.$route.query.prefieldToken;
      const dontSavePrefieldToken = this.$route.query.dontSavePrefieldToken;

      if (prefieldToken) {
        this.loginLoading = true;

        this.$api.auth.fetchPrefieldData(prefieldToken)
          .then(({ data }) => {
            if (data.payload) {
              this.username = data.payload.username;
              this.password = data.payload.password;

              if (dontSavePrefieldToken) this.savedPrefieldToken = false;
            }
          })
          .catch(err => {
            this.$errorMessageHandler(err);
          })
          .finally(() => {
            this.loginLoading = false;
          });
      } else {
        this.savedPrefieldToken = false;
      }
    }
  },
  methods: {
    checkAutoLoginToken() {
      return this.$route.path === '/verify-sign-in' && this.$route.query.token;
    },
    async handleLogin(loginData = null) {
      if (!this.$refs.form.validate() && !loginData) return;

      const sessionUsername = this.$getTabSession();

      if (sessionUsername && sessionUsername !== this.username) {
        return this.$errorMessageHandler(this.$t('notifications.loggedInAnotherTab'), this.$t('snotify.errorTitle'), { timeout: 5000 });
      }

      this.loginLoading = true;

      try {
        const payload = await login(loginData ? { token: loginData.token } : {
          username: this.username,
          password: this.password,
        });

        if (payload.isFirstTime && this.savedPrefieldToken) {
          this.$openDialog({
            component: 'confirmation',
            params: {
              persistent: true,
            },
            props: {
              type: 'info',
              text: this.$t('systemMessages.changePassword'),
              submitText: this.$t('buttons.ok'),
            },
          });
        }

        this.$setTabSession(this.username);

        await this.$router.push('/');
      } catch (err) {
        this.$errorMessageHandler(err);
      }

      this.loginLoading = false;
    },
  },
};
</script>

<style lang="scss">
  @import "src/assets/style/views/authPage";
</style>

<style lang="scss">
.login-form {
  .v-form {
    box-shadow: none;
  }
  .v-label {
    transform: translateY(-8px) scale(.75);
  }
}
</style>
